import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

export type EventArchiveCommand = "ARCHIVE_EVENT" | "RESTORE_EVENT";

/** How long to wait for the backend to settle a request before offering a retry. */
export const ARCHIVE_TIMEOUT_MS = 30_000;
export const ARCHIVE_TIMEOUT_MESSAGE =
  "This is taking longer than expected. You can try again.";

const archiveRequestSchema = z.object({
  superseded: z.boolean().optional(),
  status: z.enum(["REQUESTED", "IN_PROGRESS", "SUCCEEDED", "FAILED"]),
  error: z.string().optional(),
});

export type EventArchiveState =
  | { status: "idle" }
  | { status: "submitting"; command: EventArchiveCommand }
  | { status: "watching"; command: EventArchiveCommand }
  | { status: "succeeded"; command: EventArchiveCommand; superseded: boolean }
  | {
      status: "failed";
      error: string;
      /**
       * Set when the wait timed out rather than the request failing: which
       * request it was, so the page can tell whether the reload already
       * shows it applied.
       */
      timedOut?: EventArchiveCommand;
    };

/**
 * What one snapshot of `event_archive_requests/{id}` means for the admin, or
 * null for a document that does not parse or has not settled. A request that
 * ends SUCCEEDED as superseded is a success: the event is already in the
 * state of a later request.
 */
export function archiveOutcomeFromRequest(
  data: unknown,
  command: EventArchiveCommand,
): EventArchiveState | null {
  const parsed = archiveRequestSchema.safeParse(data);
  if (!parsed.success) return null;
  if (parsed.data.status === "SUCCEEDED") {
    return {
      status: "succeeded",
      command,
      superseded: parsed.data.superseded === true,
    };
  }
  if (parsed.data.status === "FAILED") {
    return {
      status: "failed",
      error: parsed.data.error ?? "The event could not be updated.",
    };
  }
  return null;
}

export function eventArchiveRequestDocument(
  command: EventArchiveCommand,
  eventId: string,
  uid: string,
  requestedAt: unknown,
) {
  return {
    type: command,
    eventId,
    requestedBy: uid,
    requestedAt,
    status: "REQUESTED",
  } as const;
}

/**
 * Files archive and restore requests and watches the one in flight. The
 * event's own archived state is the caller's: it comes from the page's
 * settings read, and `onSettled` is where the page reloads it. It runs when a
 * request succeeds and once when the wait times out, because a request that
 * settles after the timeout still changes the event, and the page would
 * otherwise keep showing the old state until the next action. No listener
 * stays on the event document, because a reload that reads a document with
 * an active listener can return the state from before a transaction.
 */
export function useEventArchive(
  eventId: string,
  uid: string,
  onSettled?: () => void,
) {
  const [state, setState] = useState<EventArchiveState>({ status: "idle" });
  const stopWatching = useRef<(() => void) | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filing = useRef(false);
  const mounted = useRef(true);
  // Which submit is current. A change of event starts a new generation, so
  // a request still being written for the old event drops itself when the
  // write returns instead of watching under the new one.
  const generation = useRef(0);
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  });

  const cleanup = useCallback(() => {
    stopWatching.current?.();
    stopWatching.current = null;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cleanup();
    };
  }, [cleanup]);

  // A request belongs to the event it was filed for. If the same control is
  // reused for another event, nothing from the previous one may stay on
  // screen or keep listening.
  const lastEventId = useRef(eventId);
  useEffect(() => {
    if (lastEventId.current === eventId) return;
    lastEventId.current = eventId;
    generation.current += 1;
    cleanup();
    filing.current = false;
    setState({ status: "idle" });
  }, [cleanup, eventId]);

  const submit = useCallback(
    async (command: EventArchiveCommand) => {
      // A second click while one request is in flight must not file another.
      if (filing.current) return;
      filing.current = true;
      const mine = ++generation.current;
      const current = () => mounted.current && generation.current === mine;
      cleanup();
      setState({ status: "submitting", command });
      const finish = (next: EventArchiveState) => {
        if (!current()) return;
        cleanup();
        filing.current = false;
        setState(next);
        if (next.status === "succeeded") onSettledRef.current?.();
      };
      try {
        const request = await addDoc(
          collection(db, "event_archive_requests"),
          eventArchiveRequestDocument(command, eventId, uid, serverTimestamp()),
        );
        // Unmounted, or moved to another event, while the write was in
        // flight: nothing may be left listening or counting down. The other
        // event's control owns `filing` now, so it is left alone.
        if (!current()) {
          if (!mounted.current) filing.current = false;
          return;
        }
        setState({ status: "watching", command });
        timer.current = setTimeout(() => {
          finish({
            status: "failed",
            error: ARCHIVE_TIMEOUT_MESSAGE,
            timedOut: command,
          });
          // The request may still settle later; read the event once so the
          // button follows what the backend did by now.
          onSettledRef.current?.();
        }, ARCHIVE_TIMEOUT_MS);
        stopWatching.current = onSnapshot(
          doc(db, "event_archive_requests", request.id),
          (snapshot) => {
            if (!snapshot.exists()) return;
            const next = archiveOutcomeFromRequest(snapshot.data(), command);
            if (next !== null) finish(next);
          },
          (error) => finish({ status: "failed", error: error.message }),
        );
      } catch (error) {
        finish({
          status: "failed",
          error:
            error instanceof Error
              ? error.message
              : "Could not request the change",
        });
      }
    },
    [cleanup, eventId, uid],
  );

  const reset = useCallback(() => {
    generation.current += 1;
    cleanup();
    filing.current = false;
    setState({ status: "idle" });
  }, [cleanup]);

  const pending = state.status === "submitting" || state.status === "watching";
  return { state, pending, submit, reset };
}
